import React, { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Card, CircularProgress, Pagination, Typography, useTheme } from '@mui/material';
import { ReactComponent as MagnifyingGlass } from '../../../../img/icons/warning-magnifying-glass.svg';
import { ReactComponent as WhiteMagnifyingGlass } from '../../../../img/icons/warning-magnifying-glass-white.svg';
import { UIContext } from '../../../../context/UIContext';
import { SidekickTablesContext } from '../helpers/SidekickTablesContext';

interface MobileTableProps<T> {
  isLoading: boolean;
  renderRow: (data: T) => JSX.Element;
  emptyStateTitle: string;
  emptyStateSubtitle: string;
}

const MobileTable = <T extends { id?: number; Id?: number }>({
  isLoading,
  renderRow,
  emptyStateTitle,
  emptyStateSubtitle,
}: MobileTableProps<T>) => {
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
    indexFirstDataRow: 0,
    indexLastDataRow: 10,
  });
  const [dataForPage, setDataForPage] = useState<T[]>([]);
  const { rows } = useContext(SidekickTablesContext);
  const { t } = useTranslation();
  const { colorMode } = useContext(UIContext);

  useEffect(() => {
    if (rows) {
      setDataForPage(
        rows.slice(paginationModel.indexFirstDataRow, paginationModel.indexLastDataRow)
      );
    }
  }, [rows, paginationModel]);

  const handleChangePage = (_event, newPage) => {
    const indexLastDataRow = paginationModel.pageSize * (newPage - 1) + paginationModel.pageSize;
    const indexFirstDataRow = indexLastDataRow - paginationModel.pageSize;
    setPaginationModel({
      ...paginationModel,
      page: newPage - 1,
      indexFirstDataRow,
      indexLastDataRow,
    });
  };

  if (isLoading || !rows) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignContent: 'center',
          margin: '96px',
        }}
      >
        <CircularProgress
          aria-label="Loading Data"
          aria-live="polite"
          aria-busy={isLoading}
          data-testid="loading-icon"
          sx={{ color: 'text.secondary' }}
        />
      </Box>
    );
  }

  if (rows.length === 0)
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 2,
          padding: 2,
        }}
      >
        {colorMode === 'light' ? <MagnifyingGlass /> : <WhiteMagnifyingGlass />}
        <Typography variant="h2">{t(emptyStateTitle)}</Typography>
        <Typography variant="body1">{t(emptyStateSubtitle)}</Typography>
      </Box>
    );

  return (
    <Box>
      {dataForPage?.map((rowData) => (
        <Box key={rowData.Id ?? rowData.id}>{renderRow(rowData)}</Box>
      ))}
      {dataForPage.length > 0 && rows.length > paginationModel.pageSize && (
        <Pagination
          count={Math.ceil(rows.length / paginationModel.pageSize)}
          page={paginationModel.page + 1}
          onChange={handleChangePage}
          color="primary"
          sx={{
            '.MuiPagination-ul': { justifyContent: 'center', marginY: 2 },
          }}
        />
      )}
    </Box>
  );
};

export default MobileTable;
